import { Decorative } from "@faire/design-tokens";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { CaselineProps } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/CaseSizeText/Caseline";

const getLimitedStockTextComponent = ({
  optionAvailableUnit,
  optionUnitMultiplier,
  productState,
}: Pick<
  IProductTileCaselinePropsAdaptor,
  "optionAvailableUnit" | "optionUnitMultiplier" | "productState"
>) => {
  const availableUnits = optionAvailableUnit ?? Number.POSITIVE_INFINITY;
  const unitMultiplier = optionUnitMultiplier ?? 1;

  const comesInCases = unitMultiplier !== 1;
  const availableQuantity = comesInCases
    ? Math.floor(availableUnits / unitMultiplier)
    : availableUnits;

  const isLimitedStock = availableQuantity > 0 && availableQuantity <= 3;

  const isSalesPaused = productState === IProduct.State.SALES_PAUSED;

  if (!isLimitedStock || isSalesPaused) {
    return null;
  }

  return comesInCases ? (
    <LimitedStock>
      <StrictLocalMsg
        defaultMessage="{availableQuantity, plural, one{Only # case left!} other{Only # cases left!}}"
        description={{
          text: "There are only X amount of product cases left in stock. The amount of product cases that the brand has in their inventory.",
        }}
        values={DEPRECATED_backwardsCompatibleValues({ availableQuantity })}
      />
    </LimitedStock>
  ) : (
    <LimitedStock>
      <StrictLocalMsg
        defaultMessage="Only {availableQuantity} left!"
        description={{
          text: "There are only X amount of products left in stock. The amount of products that the brand has in their inventory.",
        }}
        values={DEPRECATED_backwardsCompatibleValues({ availableQuantity })}
      />
    </LimitedStock>
  );
};

interface IProductTileCaselinePropsAdaptor {
  caseSizeText: string | undefined;
  optionAvailableUnit: number | undefined;
  optionUnitMultiplier: number | undefined;
  productState: keyof typeof IProduct.State | undefined;
  isLoggedIn: boolean;
}

export const productTileCaselinePropsAdaptor = ({
  caseSizeText,
  optionAvailableUnit,
  optionUnitMultiplier,
  productState,
  isLoggedIn,
}: IProductTileCaselinePropsAdaptor): CaselineProps => {
  const limitedStockText = getLimitedStockTextComponent({
    optionAvailableUnit,
    optionUnitMultiplier,
    productState,
  });
  const isSalesPaused = productState === IProduct.State.SALES_PAUSED;
  return {
    isLoggedIn,
    caseSizeText,
    limitedStockText,
    isSalesPaused,
  };
};

const LimitedStock = styled(Typography).attrs({
  color: Decorative.shade.orange,
})`
  width: auto;
  font-style: italic;
`;
